import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Meeting, IMeeting } from 'common/modules/ReactShared';
import {
  captureJoinError,
  captureJoinRedirect
} from 'common/modules/ReactShared/data/api/meeting/captureJoinDiagnostics';

export type tReservationError = {
  status: number | null;
  message: string;
};

function extractReservationErrorMessage(error: any): string {
  // API returns `{ errors: ["This meeting is full."] }`
  const errors = error?.response?.data?.errors;

  return Array.isArray(errors) ? `${errors[0] ?? ''}` : '';
}

const initialState: IMeeting = {
  attended: false,
  description: null,
  duration: 0,
  endTime: '',
  groupReservable: false,
  id: null,
  instructor: null,
  kind: 'other',
  languageCode: '',
  level: 'a1',
  remoteLink: '',
  reservation: false,
  startTime: '',
  state: '',
  title: '',
  supportLanguage: null
};

const getMeeting = createAsyncThunk<Meeting.MeetingApiTypes.IMeeting, number>(
  'meetings/getMeeting_request',
  async (id) => {
    const data = await Meeting.meetingApi.getMeeting(id);

    return data?.meeting;
  }
);

const createReservation = createAsyncThunk<
  Meeting.MeetingApiTypes.IMeeting,
  number,
  { rejectValue: tReservationError }
>('meetings/createReservation_request', async (id, { rejectWithValue }) => {
  try {
    const data = await Meeting.meetingApi.createReservation(id);

    return data?.meeting;
  } catch (error: any) {
    return rejectWithValue({
      status: error?.response?.status ?? null,
      message: extractReservationErrorMessage(error)
    });
  }
});

const removeReservation = createAsyncThunk<Meeting.MeetingApiTypes.IMeeting, number>(
  'meetings/removeReservation_request',
  async (id) => {
    const data = await Meeting.meetingApi.removeReservation(id);

    return data?.meeting;
  }
);

const joinClass = createAsyncThunk<string, number>(
  'meetings/joinClass_request',
  async (id) => {
    try {
      const data = await Meeting.meetingApi.joinClass(id);

      // Record the API response (the `redirectTo` we're about to open) BEFORE the
      // caller navigates the WebView away, so failing sessions carry the exact URL.
      captureJoinRedirect(id, data?.redirectTo ?? null);

      return data?.redirectTo;
    } catch (error) {
      captureJoinError(id, error);
      throw error;
    }
  }
);

const meetingSlice = createSlice({
  name: 'meeting',
  initialState: initialState as IMeeting,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(getMeeting.fulfilled, (_, action) => {
      return replaceZeroLevelInMeeting(action.payload);
    });
  }
});

const { reducer } = meetingSlice;

const actions = {
  getMeeting,
  createReservation,
  removeReservation,
  joinClass
};

export { reducer, actions };

function replaceZeroLevelInMeeting(meeting: Meeting.MeetingApiTypes.IMeeting): IMeeting {
  return {
    ...meeting,
    level: meeting.level === 'zero_level' ? 'a1' : meeting.level
  };
}
